import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { expValEquals } from '@confluence/feature-experiments';

export type SelfExcerptContext = {
	contentId: string;
	documentContentId: string | null | undefined;
	title: string | null | undefined;
	eligible: boolean;
};

const MACRO_CORE = 'com.atlassian.confluence.macro.core';
const isMacro = (node: ADFEntity, key: string): boolean =>
	node.attrs?.extensionType === MACRO_CORE && node.attrs?.extensionKey === key;

// Keep nested include resolution on its existing permission/recursion-aware path. In particular,
// do not recursively expand a source excerpt into its own macroOutput during server rendering.
const containsInclude = (node: ADFEntity | undefined): boolean =>
	!!node &&
	(isMacro(node, 'include') ||
		isMacro(node, 'excerpt-include') ||
		node.attrs?.extensionKey === 'legacy-content' ||
		(node.content?.some(containsInclude) ?? false));

/**
 * Derive render-only macroOutput from the host document. Never mutate shared parsed ADF or the
 * normalized page-body cache: subsequent body refreshes must derive from their current content.
 * Qualified title references are deliberately left to the resolver unless they exactly match the
 * host title (its first lookup). Guessing space-prefix semantics can select a different page.
 */
export const resolveSelfExcerptIncludes = (
	document: DocNode,
	{ contentId, documentContentId, title, eligible }: SelfExcerptContext,
): DocNode => {
	if (
		!eligible ||
		!contentId ||
		documentContentId !== contentId ||
		!title ||
		!expValEquals('cc_excerpt_nested_renderer_cls', 'isEnabled', true)
	) {
		return document;
	}

	const excerpts: ADFEntity[] = [];
	const collect = (node: ADFEntity | undefined): void => {
		if (!node) return;
		if (isMacro(node, 'excerpt')) {
			excerpts.push(node);
		}
		node.content?.forEach(collect);
	};
	collect(document);
	if (!excerpts.length) {
		return document;
	}

	const resolve = (node: ADFEntity | undefined): ADFEntity | undefined => {
		if (!node) return node;
		const parameters = node.attrs?.parameters;
		const params = parameters?.macroParams;
		if (isMacro(node, 'excerpt-include') && parameters && !('macroOutput' in parameters)) {
			const targetId = params?._referencedContentId?.value;
			const isSelf = targetId != null ? targetId === contentId : params?.['']?.value === title;
			const parentId = params?._parentId?.value;
			if (isSelf && (!parentId || parentId === contentId)) {
				const name = params?.name?.value;
				if (name != null && typeof name !== 'string') return node;
				const source = name
					? excerpts.find(
							(excerpt) =>
								excerpt.attrs?.parameters?.macroParams?.name?.value?.toUpperCase?.() ===
								name.toUpperCase?.(),
						)
					: excerpts[0];
				if (source?.content?.length && !source.content.some(containsInclude)) {
					return {
						...node,
						attrs: {
							...node.attrs,
							parameters: {
								...parameters,
								macroOutput: JSON.stringify({ type: 'doc', version: 1, content: source.content }),
							},
						},
					};
				}
			}
		}
		if (!node.content) {
			return node;
		}
		const content = node.content.map(resolve);
		return content.some((child, index) => child !== node.content?.[index])
			? { ...node, content }
			: node;
	};
	return resolve(document) as DocNode;
};
